import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import './Menu.css';

export interface MenuProps {
  /** The control that opens the menu. Receives the props it must spread. */
  trigger: (props: {
    'aria-expanded': boolean;
    'aria-haspopup': 'menu';
    'aria-controls': string;
    onClick: () => void;
    ref: (el: HTMLElement | null) => void;
  }) => ReactNode;
  children: ReactNode;
  /** Accessible name for the menu itself, e.g. "Account and settings". */
  label: string;
  /** Which edge the popover aligns to. `end` for topbar controls near the right. */
  align?: 'start' | 'end';
  /** Popover width; defaults to the prototype's 250px. */
  width?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A trigger + popover menu (design-system catalogue, Tier 2) — the surface behind
 * the org switcher, the avatar menu and the notification tray.
 *
 * Added by the design-fidelity audit: the prototype has three of these and the
 * build had none, which is why the org switcher had been rendered as a native
 * `<select>` (audit S-2) and the tray and avatar menu were missing outright
 * (S-5/S-6).
 *
 * Behaviour that has to be here rather than at each call site, because getting it
 * wrong is invisible until someone tries:
 * - **Escape closes and returns focus to the trigger.** Without the return, focus
 *   lands back at the top of the document and a keyboard user loses their place.
 * - **Outside pointerdown closes**, but a click *inside* does not — a menu that
 *   closes on its own contents cannot hold a form or a tab strip, and the tray
 *   holds both.
 * - **No focus trap.** These are menus, not modals; trapping focus in a menu is a
 *   common over-application of the dialog pattern and it breaks Tab-to-dismiss.
 */
export function Menu({
  trigger,
  children,
  label,
  align = 'start',
  width,
  open: controlledOpen,
  onOpenChange,
}: MenuProps) {
  const id = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const triggerRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    // `pointerdown`, not `click`: a click fires after mouseup, so a drag that
    // starts outside and ends inside would leave the menu open.
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="dpr-menu">
      {trigger({
        'aria-expanded': open,
        'aria-haspopup': 'menu',
        'aria-controls': id,
        onClick: () => setOpen(!open),
        ref: (el) => {
          triggerRef.current = el;
        },
      })}
      {open ? (
        <div
          ref={popoverRef}
          id={id}
          role="menu"
          aria-label={label}
          className={`dpr-menu__popover dpr-menu__popover--${align}`}
          style={width ? { minInlineSize: `${width}px` } : undefined}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function MenuSection({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="dpr-menu__section">
      {label ? <span className="dpr-menu__label">{label}</span> : null}
      {children}
    </div>
  );
}

export interface MenuItemProps {
  children: ReactNode;
  icon?: ReactNode;
  /**
   * A character from the sanctioned `MENU_GLYPHS` table — the prototype's mono
   * glyph column. Mutually exclusive with `icon` in practice; if both are given,
   * the glyph wins because it is the menu's native treatment.
   */
  glyph?: string;
  /** Right-aligned secondary text — a shortcut, a plan name, a count. */
  hint?: ReactNode;
  onSelect?: () => void;
  href?: string;
  tone?: 'default' | 'danger';
  /** Marks the current selection, e.g. the active org. */
  selected?: boolean;
}

export function MenuItem({
  children,
  icon,
  glyph,
  hint,
  onSelect,
  href,
  tone = 'default',
  selected,
}: MenuItemProps) {
  const Element = href ? 'a' : 'button';
  return (
    <Element
      className={['dpr-menu__item', tone === 'danger' ? 'dpr-menu__item--danger' : null]
        .filter(Boolean)
        .join(' ')}
      // `menuitemradio` where the item represents a *choice among alternatives*
      // (which org am I in), plain `menuitem` where it is an action. The
      // distinction is what makes a screen reader announce the current org as
      // selected rather than as just another link.
      role={selected != null ? 'menuitemradio' : 'menuitem'}
      aria-checked={selected != null ? selected : undefined}
      href={href}
      type={href ? undefined : 'button'}
      onClick={onSelect}
    >
      {glyph ? (
        <span className="dpr-menu__item-glyph" aria-hidden="true">
          {glyph}
        </span>
      ) : icon ? (
        <span className="dpr-menu__item-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
      {hint ? <span className="dpr-menu__item-hint">{hint}</span> : null}
    </Element>
  );
}
